import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, X, Check, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { mapThumbUrl } from "@/components/PassageMaps";

const MAX = 3;
const REF_RX = /\b((?:[1-3]\s)?[A-Z][a-z]+(?:\s(?:of\s)?[A-Z][a-z]+)?)\s+(\d{1,3})(?::\d{1,3})?/g;

export const findRefs = (text) => [...(text || "").matchAll(REF_RX)].map((m) => m[0]).slice(0, 2);

const fetchMaps = (term) => {
  const ref = findRefs(term)[0];
  return ref
    ? api.get("/maps/match", { params: { reference: ref, limit: 8 } }).then((r) => r.data)
    : api.get("/maps/browse", { params: { place: term, limit: 8 } }).then((r) => r.data.items);
};

const Tile = ({ m, picked, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`relative text-left rounded-xl overflow-hidden border transition-colors ${picked ? "border-gold ring-2 ring-gold" : "border-border hover:border-gold"}`}>
    <img src={mapThumbUrl(m.id)} alt={m.title} loading="lazy" className="w-full aspect-[4/3] object-cover bg-ink/5" />
    <p className="text-[11px] leading-tight p-1.5 text-ink/75 line-clamp-2 bg-card">{m.title}</p>
    {picked && <span className="absolute top-1 right-1 bg-gold text-ink rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
  </button>
);

export const MapPicker = ({ text, selected, onChange }) => {
  const [suggested, setSuggested] = useState([]);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const refKey = findRefs(text).join("|");

  useEffect(() => {
    if (!refKey) return setSuggested([]);
    const t = setTimeout(() => {
      api.get("/maps/match", { params: { reference: refKey.split("|")[0], limit: 4 } })
        .then(({ data }) => setSuggested(data)).catch(() => setSuggested([]));
    }, 600);
    return () => clearTimeout(t);
  }, [refKey]);

  useEffect(() => {
    if (term.trim().length < 2) return setResults([]);
    const t = setTimeout(() => fetchMaps(term.trim()).then(setResults).catch(() => setResults([])), 400);
    return () => clearTimeout(t);
  }, [term]);

  const toggle = (m) => {
    if (selected.some((s) => s.id === m.id)) return onChange(selected.filter((s) => s.id !== m.id));
    if (selected.length >= MAX) return toast.error(`You can attach up to ${MAX} maps`);
    onChange([...selected, m]);
  };
  const isPicked = (m) => selected.some((s) => s.id === m.id);

  return (
    <div data-testid="map-picker" className="rounded-2xl border border-border bg-card/60 p-4 space-y-3">
      <p className="text-sm font-600 text-ink">Attach Bible maps <span className="font-400 text-ink/50">(optional, up to {MAX})</span></p>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="map-picker-selected">
          {selected.map((m) => (
            <span key={m.id} className="inline-flex items-center gap-1 bg-gold-light border border-gold/40 rounded-full pl-3 pr-1 py-1 text-xs text-ink">
              {m.title}
              <button type="button" onClick={() => toggle(m)} data-testid={`map-picker-remove-${m.id}`} className="p-0.5 hover:text-red-700"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      {suggested.length > 0 && (
        <div data-testid="map-picker-suggested">
          <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-gold mb-2"><Sparkles className="w-3 h-3" /> Suggested for {refKey.split("|")[0]}</p>
          <div className="grid grid-cols-4 gap-2">
            {suggested.map((m) => <Tile key={m.id} m={m} picked={isPicked(m)} onClick={() => toggle(m)} testid={`map-picker-suggest-${m.id}`} />)}
          </div>
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" />
        <Input data-testid="map-picker-search" value={term} onChange={(e) => setTerm(e.target.value)}
          placeholder="Search maps by place or verse (e.g. Jerusalem, John 6)" className="pl-9 bg-card border-ink/15 h-10" />
      </div>
      {results.length > 0 && (
        <div className="grid grid-cols-4 gap-2" data-testid="map-picker-results">
          {results.map((m) => <Tile key={m.id} m={m} picked={isPicked(m)} onClick={() => toggle(m)} testid={`map-picker-result-${m.id}`} />)}
        </div>
      )}
      {term.trim().length >= 2 && results.length === 0 && (
        <p data-testid="map-picker-empty" className="text-xs text-ink/50">No maps found yet — try another place or verse.</p>
      )}
    </div>
  );
};
